import styled, { css } from 'styled-components';
import {
  TableCell,
  Typography,
  TableRow,
} from '@mui/material';

import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';

import { ReactComponent as ChartIcon } from '../../../assets/icons/chart.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit_icon.svg';
import colors from '../../../styles/colors';
import Button from '../../../components/Button';

const Container = styled.div`
  display: flex;
  flex-direction: row;
`;

const DataContainer = styled.div`
  margin: 10px;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
`;

const StyledWeightCell = styled(TableCell)`
  font-size: 24px;
  font-weight: bold;
`;

const StyledTableRow = styled(TableRow)`
  &:nth-of-type(odd) {
    background-color: ${colors.base.alpha};
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 10px;
  width: calc(100% - 20px);
`;

const StyledLink = styled.a`
  cursor: pointer;
`;

const StatContainer = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${colors.base.gamma};
  border-radius: 5px;
  border: 1px solid ${colors.shades.gamma4};
  margin: 10px 6px;
`;

const WeightStat = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.base.gamma};
  padding: 16px;
  width: ${({ $width }) => ($width || '100%')};
  ${({ showDivider }) => (showDivider && css`
    border-right: 1px solid ${colors.shades.gamma5};
  `)}
`;

const WeightStatContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 30px;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
`;

const WeightStatTitle = styled(Typography)`
  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
  text-transform: uppercase;
  margin: 0 30px 5px;
  color: ${colors.shades.gamma10};
`;

const WeightStatValue = styled(Typography)`
  font-size: 16px;
  font-weight: 800;
  line-height: 20px;
  color: ${(props) => (props.$current ? colors.base.beta : colors.shades.gamma10)};
`;

const StatLabel = styled(Typography)`
  font-size: 10px;
  font-weight: 400;
  line-height: 12px;
  color: ${colors.shades.gamma10};
`;

// Up vs down, not good vs bad — an increase is only "positive" arithmetically.
//
// These two figures are read against each other at a glance, so they need two hues,
// not two weights of one. The retheme rotated secondary2 from light blue to green,
// which left gain and loss both green; moving loss to a neutral grey fixed the contrast
// but cost the distinction, and coaches noticed (S2-9035).
//
// Restores the original green/blue pairing one shade deeper, so it also clears AA on
// the #F4F7F5 stat block — the values it restores did not: green #00AE07 was 2.76:1 and
// blue #4E88FD was 3.11:1, against 4.84:1 and 4.68:1 here.
const DifferenceStatValue = styled(Typography)`
  font-size: 16px;
  font-weight: 800;
  line-height: 20px;
  color: ${(props) => (props.$isPositive ? colors.shades.success7 : colors.shades.info1)};
`;

const DifferenceStatLabel = StatLabel;

const CheckInQuestionContainer = Stat;

const QuestionText = styled(Typography)`
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  color: ${colors.shades.gamma10};
`;

const AnswerText = styled(Typography)`
  background: ${colors.base.alpha};
  padding: 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  margin: 5px 0 14px;
`;

const StatValue = styled(Typography)`
  font-size: 12px;
  font-weight: 450;
  line-height: 15px;
  color: ${(props) => (props.$current ? colors.base.beta : colors.shades.gamma10)};
`;

const StyledStatCell = styled(TableCell)`
  padding: 8px;
  border-bottom: 1px solid ${colors.shades.gamma1};
  text-align: left;
`;

const StyledTitle = styled(Typography)`
  display: flex;
  font-size: 14px;
  line-height: 16px;
  padding: 8px 10px;
`;

const StyledIconButton = styled(Button)`
  display: flex;
  padding: 0px 7.5px;
  align-self: flex-end;
  background-color: ${colors.shades.gamma8};
  color: ${colors.base.beta};
  height: 30px;
  width: 30px;
  border-radius: 20px;
  cursor: pointer;

  &:hover {
    background-color: ${colors.shades.gamma1};
  }
`;

/*
  The question label and its action on one line, so the answer box below keeps the full width. The
  container is a column, so without this the button would sit on its own row.
*/
const QuestionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 8px;
`;

// `StyledIconButton` bottom-aligns itself, which reads as misaligned on a single-line row.
const AnswerActionButton = styled(StyledIconButton)`
  align-self: center;
  flex-shrink: 0;
`;

// The same megaphone the photo action uses, so both entry points read as the same thing.
const StyledHighlightIcon = styled(CampaignOutlinedIcon)`
  width: 15px;
  height: 15px;
`;

const StyledChartIcon = styled(ChartIcon)`
  width: 15px;
  height: 15px;
`;

const StyledEditIcon = styled(EditIcon)`
  width: 15px;
  height: 15px;
`;

export {
  Container,
  DataContainer,
  ImageContainer,
  StyledContainer,
  StyledLink,
  StyledWeightCell,
  StatContainer,
  Stat,
  WeightStatContainer,
  WeightStatTitle,
  WeightStatValue,
  StatLabel,
  DifferenceStatValue,
  DifferenceStatLabel,
  CheckInQuestionContainer,
  QuestionText,
  AnswerText,
  StatValue,
  StyledStatCell,
  StyledTableRow,
  StyledTitle,
  WeightStat,
  StyledIconButton,
  QuestionRow,
  AnswerActionButton,
  StyledHighlightIcon,
  StyledChartIcon,
  StyledEditIcon,
};
